/**
 * Custom Hooks for Candidate Ratings
 * Provides React hooks for managing candidate evaluation scores
 */

import { useCallback } from 'react';
import { useResourceCollection, useResourceById, useCreateResource, useUpdateResource, useDeleteResource, ResourceState, MutationState } from './useResource';
import { candidateRatingsService } from '../services';
import { CandidateRating } from '../types';
import { ResourceQueryOptions } from '../services/resources.service';

/**
 * Hook to fetch all candidate ratings
 */
export function useCandidateRatings(
  options?: ResourceQueryOptions
): ResourceState<CandidateRating> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      candidateRatingsService.getAll(opts) as unknown as Promise<{ data: CandidateRating[]; meta?: any }>,
    []
  );
  return useResourceCollection<CandidateRating>(fetchFn, options);
}

/**
 * Hook to fetch a single candidate rating by ID
 */
export function useCandidateRating(id: number | null, options?: ResourceQueryOptions) {
  return useResourceById<CandidateRating>((id) => candidateRatingsService.getById(id, options), id);
}

/**
 * Hook to create a new candidate rating
 */
export function useCreateCandidateRating(onSuccess?: () => void): MutationState<CandidateRating> {
  return useCreateResource<CandidateRating>(candidateRatingsService.create, onSuccess);
}

/**
 * Hook to update an existing candidate rating
 */
export function useUpdateCandidateRating(onSuccess?: () => void): MutationState<CandidateRating> {
  return useUpdateResource<CandidateRating>(candidateRatingsService.update, onSuccess);
}

/**
 * Hook to delete a candidate rating
 */
export function useDeleteCandidateRating(onSuccess?: () => void): MutationState<void> {
  return useDeleteResource(candidateRatingsService.delete, onSuccess);
}

